(function () {
  if (window.__klipProductDiscountLoaded) {
    if (typeof window.initKlipProductDiscounts === 'function') {
      window.initKlipProductDiscounts();
    }
    return;
  }

  window.__klipProductDiscountLoaded = true;

  const ROOT_SELECTOR = "product-info[id^='MainProduct']";
  const PRICE_WRAPPER_SELECTOR = '[data-klip-price-wrapper]';
  const CUSTOM_COUPON_SELECTOR = '.seed-klip-coupon';
  const CUSTOM_PRICE_SELECTOR = '.seed-klip-price';
  const SOURCE_HIDDEN_CLASS = 'seed-klip-source-hidden';
  const CODE_BLOCKLIST = new Set([
    'SAVE',
    'OFF',
    'FOR',
    'THIS',
    'PRODUCT',
    'COPY',
    'CODE',
    'COUPON',
    'DISCOUNT',
    'CHECKOUT',
    'GET',
    'USE',
    'THE',
    'USD',
  ]);

  function calculateDiscountedCents(originalCents, percent) {
    const discountCents = Math.floor((originalCents * percent) / 100);
    return Math.max(originalCents - discountCents, 0);
  }

  class KlipProductDiscount {
    constructor(root) {
      this.root = root;
      this.sourceElement = null;
      this.copyButton = null;
      this.lastCoupon = null;
      this.refreshTimeout = null;
      this.toastTimeout = null;
      this.isRefreshing = false;
      this.observer = new MutationObserver(() => this.scheduleRefresh());
      this.handleDynamicCheckoutClick = this.handleDynamicCheckoutClick.bind(this);

      this.observer.observe(this.root, {
        childList: true,
        subtree: true,
        characterData: true,
      });

      this.root.addEventListener('click', this.handleDynamicCheckoutClick, true);
      this.refresh();
    }

    scheduleRefresh() {
      window.clearTimeout(this.refreshTimeout);
      this.refreshTimeout = window.setTimeout(() => this.refresh(), 80);
    }

    refresh() {
      if (this.isRefreshing) return;
      this.isRefreshing = true;
      this.observer.disconnect();

      const coupon = this.findCoupon();

      if (!coupon) {
        if (this.lastCoupon && this.isCurrentVariantSoldOut()) {
          this.renderPrice({
            ...this.lastCoupon,
            soldOut: true,
          });
          this.renderCoupon({
            ...this.lastCoupon,
            soldOut: true,
          });
          this.publishDiscount(this.lastCoupon);
          this.observe();
          this.isRefreshing = false;
          return;
        }

        this.restore();
        this.observe();
        this.isRefreshing = false;
        return;
      }

      this.sourceElement = coupon.sourceElement;
      this.copyButton = coupon.copyButton;
      this.lastCoupon = {
        code: coupon.code,
        percent: coupon.percent,
      };
      this.sourceElement.classList.add(SOURCE_HIDDEN_CLASS);
      this.sourceElement.setAttribute('aria-hidden', 'true');

      this.renderPrice(coupon);
      this.renderCoupon(coupon);
      this.publishDiscount(coupon);
      this.observe();
      this.isRefreshing = false;
    }

    observe() {
      this.observer.observe(this.root, {
        childList: true,
        subtree: true,
        characterData: true,
      });
    }

    restore() {
      this.root.querySelector(CUSTOM_COUPON_SELECTOR)?.remove();
      this.root.querySelector(CUSTOM_PRICE_SELECTOR)?.remove();
      this.root.querySelector(PRICE_WRAPPER_SELECTOR)?.classList.remove('seed-klip-price-active');
      this.publishDiscount(null);

      if (this.sourceElement?.isConnected) {
        this.sourceElement.classList.remove(SOURCE_HIDDEN_CLASS);
        this.sourceElement.removeAttribute('aria-hidden');
      }

      this.sourceElement = null;
      this.copyButton = null;
    }

    destroy() {
      window.clearTimeout(this.refreshTimeout);
      window.clearTimeout(this.toastTimeout);
      this.observer.disconnect();
      this.root.removeEventListener('click', this.handleDynamicCheckoutClick, true);
    }

    publishDiscount(coupon) {
      const code = coupon?.code || '';
      const percent = Number.parseFloat(coupon?.percent || 0);

      if (code && Number.isFinite(percent) && percent > 0) {
        this.root.dataset.seedKlipDiscountCode = code;
        this.root.dataset.seedKlipDiscountPercent = String(percent);
      } else {
        delete this.root.dataset.seedKlipDiscountCode;
        delete this.root.dataset.seedKlipDiscountPercent;
      }

      this.syncProductFormDiscount(code);

      this.root.dispatchEvent(
        new CustomEvent('seed:klip-discount', {
          bubbles: true,
          detail: {
            code,
            percent: Number.isFinite(percent) ? percent : 0,
          },
        })
      );
    }

    syncProductFormDiscount(code) {
      const forms = Array.from(
        this.root.querySelectorAll(
          'form[data-klip-product-form][data-type="add-to-cart-form"], product-form[data-klip-product-form] form[data-type="add-to-cart-form"]'
        )
      );

      forms.forEach((form) => {
        let input = form.querySelector('input[name="discount"]');
        const configuredCode = String(form.dataset.seedProductDiscountCode || '').trim();

        if (code) {
          if (!input) {
            input = document.createElement('input');
            input.type = 'hidden';
            input.name = 'discount';
            input.dataset.seedKlipDiscountInput = 'true';
            form.append(input);
          }

          input.value = code;
          return;
        }

        if (configuredCode) {
          if (!input) {
            input = document.createElement('input');
            input.type = 'hidden';
            input.name = 'discount';
            form.append(input);
          }
          input.value = configuredCode;
          delete input.dataset.seedKlipDiscountInput;
        } else if (input?.dataset.seedKlipDiscountInput === 'true') {
          input.remove();
        }
      });
    }

    async handleDynamicCheckoutClick(event) {
      const button = this.getDynamicCheckoutButton(event);
      if (!button || button.disabled || button.getAttribute('aria-disabled') === 'true') return;

      const form = button.closest('form') || this.root.querySelector('form[data-type="add-to-cart-form"]');
      const code = this.getCurrentDiscountCode(form);
      const formData = this.getCartAddFormData(form, code);
      if (!code || !formData) return;

      const discountToApply = this.isCartDiscountCompatible(code) ? code : null;

      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();

      button.setAttribute('aria-disabled', 'true');
      button.disabled = true;

      try {
        await this.addProductToCart(formData);
        if (discountToApply !== null) await this.updateCartDiscount(discountToApply);

        window.location.assign(discountToApply ? this.buildDiscountCheckoutUrl(code) : '/checkout');
      } catch (error) {
        this.showProductFormError(form, error);
        button.disabled = false;
        button.removeAttribute('aria-disabled');
      }
    }

    getDynamicCheckoutButton(event) {
      const targetButton = event.target?.closest?.('.shopify-payment-button__button--unbranded');
      if (targetButton && this.root.contains(targetButton)) return targetButton;

      const path = typeof event.composedPath === 'function' ? event.composedPath() : [];
      return (
        path.find(
          (node) =>
            node instanceof Element &&
            this.root.contains(node) &&
            node.matches('.shopify-payment-button__button--unbranded')
        ) || null
      );
    }

    getCurrentDiscountCode(form) {
      const datasetCode = this.root.dataset.seedKlipDiscountCode || '';
      if (datasetCode) return datasetCode;

      if (this.lastCoupon?.code) return this.lastCoupon.code;

      const formCode = form?.dataset.seedProductDiscountCode || form?.querySelector('input[name="discount"]')?.value || '';
      if (formCode) return formCode;

      const coupon = this.findCoupon();
      return coupon?.code || '';
    }

    getCartAddFormData(form, code) {
      if (!(form instanceof HTMLFormElement) || !code) return null;

      const formData = new FormData(form);
      const variantId = String(formData.get('id') || '').trim();
      if (!/^\d+$/.test(variantId)) return null;

      formData.delete('discount');
      formData.set('properties[_seed_coupon_code]', code);
      if (!formData.get('quantity')) formData.set('quantity', '1');
      return formData;
    }

    addProductToCart(formData) {
      const config = typeof fetchConfig === 'function'
        ? fetchConfig('javascript')
        : { method: 'POST', headers: { Accept: 'application/javascript' } };
      config.headers = config.headers || {};
      config.headers['X-Requested-With'] = 'XMLHttpRequest';
      delete config.headers['Content-Type'];
      config.body = formData;

      return fetch(window.routes?.cart_add_url || '/cart/add', config).then(async (response) => {
        const result = await response.json();
        if (!response.ok || result.status) {
          throw new Error(result.description || result.message || window.cartStrings?.error || '');
        }
        return result;
      });
    }

    updateCartDiscount(code) {
      return fetch(window.routes?.cart_update_url || '/cart/update', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ discount: code }),
      }).then(async (response) => {
        const cart = await response.json();
        const requestedDiscount = code
          ? cart.discount_codes?.find((discount) => String(discount.code || '').toUpperCase() === code.toUpperCase())
          : null;

        if (!response.ok || cart.status || requestedDiscount?.applicable === false) {
          throw new Error(cart.description || cart.message || window.cartStrings?.error || '');
        }

        return cart;
      });
    }

    isCartDiscountCompatible(code) {
      const normalizedCode = String(code || '').trim().toUpperCase();
      if (!normalizedCode) return false;

      return Array.from(document.querySelectorAll('[data-seed-cart-discount-item]')).every((item) => {
        const itemCode = String(item.dataset.seedCartCouponCode || '').trim().toUpperCase();
        return !itemCode || itemCode === normalizedCode;
      });
    }

    buildDiscountCheckoutUrl(code) {
      return `/discount/${encodeURIComponent(code)}?redirect=/checkout`;
    }

    showProductFormError(form, error) {
      const productForm = form?.closest('product-form');
      if (typeof productForm?.handleErrorMessage === 'function') {
        productForm.handleErrorMessage(error?.message || window.cartStrings?.error || '');
      }
    }

    findCoupon() {
      const appBlock = this.root.querySelector('[data-klip-app-block]');
      const blocks = appBlock ? [appBlock, this.root] : [this.root];

      for (const block of blocks) {
        const couponFromBlock = this.findCouponInBlock(block);
        if (couponFromBlock) return couponFromBlock;
      }

      return null;
    }

    findCouponInBlock(block) {
      const blockText = this.getText(block);
      const blockPercent = this.extractPercent(blockText);
      const blockCode = blockPercent ? this.extractCode(block, block) : null;

      if (blockPercent && blockCode && blockText.length <= 900) {
        return {
          code: blockCode,
          percent: blockPercent,
          sourceElement: block,
          copyButton: block.querySelector('button, [role="button"], a, input[type="button"]'),
        };
      }

      const buttons = Array.from(block.querySelectorAll('button, [role="button"], a, input[type="button"]'));

      for (const button of buttons) {
        if (button.closest(CUSTOM_COUPON_SELECTOR)) continue;
        if (button.closest(PRICE_WRAPPER_SELECTOR)) continue;

        const buttonText = this.getText(button);
        const buttonValue = button.value || '';
        const looksLikeCopyButton = /copy|coupon|code/i.test(`${buttonText} ${buttonValue}`);

        if (!looksLikeCopyButton) continue;

        const sourceElement = this.getCouponSource(button);
        if (!sourceElement) continue;

        const sourceText = this.getText(sourceElement);
        const percent = this.extractPercent(sourceText);
        const code = this.extractCode(sourceElement, button);

        if (!percent || !code) continue;

        return {
          code,
          percent,
          sourceElement,
          copyButton: button,
        };
      }

      return null;
    }

    getCouponSource(button) {
      let current = button.parentElement;
      let best = null;

      for (let depth = 0; current && current !== this.root && depth < 9; depth += 1) {
        if (current.closest(CUSTOM_COUPON_SELECTOR)) break;
        if (current.matches(PRICE_WRAPPER_SELECTOR)) break;

        const text = this.getText(current);

        if (/%/.test(text) && text.length <= 700) {
          best = current;
        }

        if (/%/.test(text) && /off|save|discount|coupon|code|product/i.test(text) && text.length <= 420) {
          return current;
        }

        current = current.parentElement;
      }

      return best;
    }

    extractPercent(text) {
      const match = text.match(/(\d+(?:\.\d+)?)\s*%/);
      if (!match) return null;

      const percent = Number.parseFloat(match[1]);
      if (!Number.isFinite(percent) || percent <= 0 || percent >= 100) return null;

      return percent;
    }

    extractCode(sourceElement, button) {
      const attributeCode = this.getCodeFromAttributes(button) || this.getCodeFromAttributes(sourceElement);
      if (attributeCode) return attributeCode;

      const codeElement = Array.from(sourceElement.querySelectorAll('[data-code], [data-coupon-code], [data-discount-code]'))
        .map((element) => this.getCodeFromAttributes(element))
        .find(Boolean);

      if (codeElement) return codeElement;

      const text = this.getText(sourceElement);
      const tokens = text.match(/\b[A-Za-z0-9][A-Za-z0-9_-]{3,}\b/g) || [];

      return tokens.find((token) => !CODE_BLOCKLIST.has(token.toUpperCase())) || null;
    }

    getCodeFromAttributes(element) {
      const attributes = [
        'data-code',
        'data-coupon-code',
        'data-discount-code',
        'data-clipboard-text',
        'data-clipboard-value',
        'aria-label',
        'title',
      ];

      for (const attribute of attributes) {
        const value = element.getAttribute?.(attribute);
        const code = this.extractCodeFromString(value);
        if (code) return code;
      }

      return null;
    }

    extractCodeFromString(value) {
      if (!value) return null;

      const tokens = String(value).match(/\b[A-Za-z0-9][A-Za-z0-9_-]{3,}\b/g) || [];
      return tokens.find((token) => !CODE_BLOCKLIST.has(token.toUpperCase())) || null;
    }

    renderPrice({ percent, soldOut = false }) {
      const priceWrapper = this.root.querySelector(PRICE_WRAPPER_SELECTOR);
      const nativePrice = priceWrapper?.querySelector('.price');

      if (!priceWrapper || !nativePrice) return;

      const originalCents = Number.parseInt(nativePrice.dataset.priceCents, 10);
      const currencyCode = nativePrice.dataset.currencyCode || window.Shopify?.currency?.active || 'USD';

      if (!Number.isFinite(originalCents) || originalCents <= 0) return;

      const discountedCents = calculateDiscountedCents(originalCents, percent);
      const roundedPercent = this.formatPercent(percent);
      const badgeText = soldOut ? 'Sold out' : `Save ${roundedPercent}`;
      let priceRow = priceWrapper.querySelector(CUSTOM_PRICE_SELECTOR);

      if (!priceRow) {
        priceRow = document.createElement('div');
        priceRow.className = 'seed-klip-price';
        priceRow.setAttribute('aria-live', 'polite');
        priceWrapper.append(priceRow);
      }

      priceWrapper.classList.add('seed-klip-price-active');
      priceRow.classList.toggle('is-sold-out', soldOut);
      priceRow.innerHTML = `
        <span class="seed-klip-price__sale">${this.formatMoney(discountedCents, currencyCode)}</span>
        <s class="seed-klip-price__regular">${this.formatMoney(originalCents, currencyCode)}</s>
        <span class="seed-klip-price__badge">${badgeText}</span>
      `;
    }

    renderCoupon({ code, percent, soldOut = false }) {
      const priceWrapper = this.root.querySelector(PRICE_WRAPPER_SELECTOR);
      if (!priceWrapper) return;

      let couponElement = this.root.querySelector(CUSTOM_COUPON_SELECTOR);

      if (!couponElement) {
        couponElement = document.createElement('div');
        couponElement.className = 'seed-klip-coupon';
        priceWrapper.insertAdjacentElement('afterend', couponElement);
      }

      const roundedPercent = this.formatPercent(percent);
      const headline = soldOut ? 'Sold out' : `Save ${roundedPercent}`;

      couponElement.innerHTML = `
        <div class="seed-klip-coupon__card">
          <span class="seed-klip-coupon__icon" aria-hidden="true">
            <svg viewBox="0 0 24 24" focusable="false">
              <path d="M20.5 11.8 12.2 20a2.2 2.2 0 0 1-3.1 0L4 14.9a2.2 2.2 0 0 1 0-3.1l8.3-8.3H20v7.7Z"/>
              <circle cx="16.9" cy="7.1" r="1.4"/>
            </svg>
          </span>
          <div class="seed-klip-coupon__topline">
            <strong>${headline}</strong>
            <button class="seed-klip-coupon__copy" type="button">Copy Code</button>
          </div>
          <p>Use the code <span>${this.escapeHTML(code)}</span> at checkout to get ${roundedPercent} off</p>
        </div>
      `;

      couponElement.querySelector('.seed-klip-coupon__copy')?.addEventListener('click', (event) => {
        this.copyCouponCode(code, event.currentTarget);
      });
    }

    copyCouponCode(code, button) {
      navigator.clipboard?.writeText(code).catch(() => {});
      this.showCopyToast();
    }

    showCopyToast() {
      let toast = this.root.querySelector('.seed-klip-copy-toast');

      if (!toast) {
        toast = document.createElement('div');
        toast.className = 'seed-klip-copy-toast';
        toast.setAttribute('role', 'status');
        toast.setAttribute('aria-live', 'polite');
        toast.textContent = 'Discount code copied';
        this.root.append(toast);
      }

      window.clearTimeout(this.toastTimeout);
      toast.classList.add('is-visible');
      this.toastTimeout = window.setTimeout(() => {
        toast.classList.remove('is-visible');
      }, 1800);
    }

    formatPercent(percent) {
      return `${Number.isInteger(percent) ? percent : percent.toFixed(1)}%`;
    }

    formatMoney(cents, currencyCode) {
      const amount = cents / 100;

      try {
        return new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
          style: 'currency',
          currency: currencyCode,
          currencyDisplay: 'narrowSymbol',
        }).format(amount);
      } catch (error) {
        return `$${amount.toFixed(2)}`;
      }
    }

    isCurrentVariantSoldOut() {
      const nativePrice = this.root.querySelector(`${PRICE_WRAPPER_SELECTOR} .price`);
      return nativePrice?.classList.contains('price--sold-out');
    }

    getText(element) {
      return (element?.textContent || '').replace(/\s+/g, ' ').trim();
    }

    escapeHTML(value) {
      return String(value)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
    }
  }

  function getScopedRoots(source) {
    const scope = source?.target || source || document;

    if (scope instanceof Element) {
      if (scope.matches(ROOT_SELECTOR)) return [scope];
      return Array.from(scope.querySelectorAll(ROOT_SELECTOR));
    }

    return Array.from(document.querySelectorAll(ROOT_SELECTOR));
  }

  function initKlipProductDiscounts(source) {
    getScopedRoots(source).forEach((root) => {
      if (root.seedKlipProductDiscount) {
        root.seedKlipProductDiscount.scheduleRefresh();
        return;
      }

      root.seedKlipProductDiscount = new KlipProductDiscount(root);
    });
  }

  window.initKlipProductDiscounts = initKlipProductDiscounts;

  document.addEventListener('DOMContentLoaded', initKlipProductDiscounts);
  document.addEventListener('shopify:section:load', initKlipProductDiscounts);
  document.addEventListener('variant:change', initKlipProductDiscounts);
  document.addEventListener('product-info:loaded', initKlipProductDiscounts);
  window.addEventListener('load', initKlipProductDiscounts);

  if (document.readyState !== 'loading') {
    initKlipProductDiscounts();
  }

  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.variantChange, () => {
      window.setTimeout(initKlipProductDiscounts, 120);
    });
  }
})();
